import { memo, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { Button } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { SuggestionRow } from "../helpCenter/aggregate-proposals"
import type { AgentDocsGroup } from "./agent-items"
import type { ArticleLike } from "./docs-change"
import type { DocsGroupState } from "./use-recommendation-actions"
import { useShortcut } from "@/lib"
import { useRole } from "@/lib/useRole"
import { ProposalSources } from "../helpCenter/proposal-sources"
import { proposalRowTime } from "./agent-items"
import { AgentMessage } from "./agent-message-header"
import { DecisionBar } from "./decision-bar"
import { ButtonKeys, DeclineButton, isTyping } from "./decline-button"
import { useDocsSaves, useProposalGroupRows } from "./docs-change"
import { DocsChangeText } from "./docs-change-text"
import { docsSuggestionIntro, proposalIntro } from "./format"
import { useDocsSuggestionActions } from "./use-docs-suggestion-actions"
import { DOCS_DECLINE_REASONS } from "./use-recommendation-actions"

// A docs suggestion as the agent's first message: why it wrote the change, the
// change itself, and the decision inside the card, like an idea.
export const DocsSuggestionMessage = memo(
	({ group, onClosed }: { group: AgentDocsGroup; onClosed: () => void }) => {
		const docs = useProposalGroupRows(group.id)
		return (
			<DocsMessage
				intro={docsSuggestionIntro(group.source)}
				at={group.createdAt}
				proposalGroupId={group.id}
				group={docs.group}
				rows={docs.rows}
				articles={docs.articles}
				loaded={docs.loaded}
				onClosed={onClosed}
			/>
		)
	},
)

// A teammate's suggested edit or an API draft, which no group holds.
export const ProposalSuggestionMessage = memo(
	({ row, onClosed }: { row: SuggestionRow; onClosed: () => void }) => {
		const [members] = useQuery(queries.teamMembers())
		const [open] = useQuery(queries.openProposalsForWorkspace())
		const authorId = row.proposals[0]?.createdByUserId
		const authorName = members.find((m) => m.user?.id === authorId)?.user?.name
		const rows = useMemo(() => [row], [row])
		const articles = useMemo(() => {
			const byId = new Map<string, ArticleLike>()
			for (const proposal of open) {
				const article = proposal.article as ArticleLike | null | undefined
				if (article) byId.set(article.id, article)
			}
			return byId
		}, [open])
		return (
			<DocsMessage
				intro={proposalIntro({
					authorName,
					isOrphanDraft: row.isOrphanDraft,
				})}
				at={proposalRowTime(row)}
				proposalGroupId={null}
				group={null}
				rows={rows}
				articles={articles}
				loaded
				onClosed={onClosed}
			/>
		)
	},
)

const DocsMessage = memo(
	({
		intro,
		at,
		proposalGroupId,
		group,
		rows,
		articles,
		loaded,
		onClosed,
	}: {
		intro: string
		at: number
		proposalGroupId: string | null
		group: DocsGroupState | null | undefined
		rows: SuggestionRow[]
		articles: ReadonlyMap<string, ArticleLike>
		loaded: boolean
		onClosed: () => void
	}) => {
		const { role } = useRole()
		const canAct = role !== "VIEWER"
		const saves = useDocsSaves()
		const { accept, decline, busy } = useDocsSuggestionActions({
			proposalGroupId,
			group,
			rows,
			saves,
			onClosed,
		})
		const firstRow = rows[0]
		const firstProposal = firstRow?.proposals[0]
		const missing = loaded && !firstRow
		useShortcut("1", () => void accept(), {
			enabled: canAct && !missing && !busy,
			ignoreEventWhen: isTyping,
		})

		return (
			<div data-testid="agent-docs-message" className="px-1 pb-6">
				<AgentMessage at={at}>
					<div className="flex min-w-0 flex-col gap-4 pt-1">
						<div className="flex flex-col gap-3 text-sm leading-6 text-primary">
							<p>{intro}</p>
							{firstProposal?.description && (
								<p data-testid="agent-docs-summary">
									{firstProposal.description}
								</p>
							)}
						</div>
						{firstProposal && firstProposal.sources.length > 0 && (
							<ProposalSources sources={firstProposal.sources} />
						)}
						{/* The same card as a recommendation: the added text, the full diff behind Preview. */}
						<DocsChangeText rows={rows} articles={articles} loaded={loaded} />
						{canAct && !missing && (
							<DecisionBar>
								<DeclineButton
									label="Decline"
									reasons={DOCS_DECLINE_REASONS}
									disabled={busy}
									shortcut
									onDecline={(reason, note) => void decline(reason, note)}
								/>
								<Button
									isLoading={busy}
									disabled={busy}
									onClick={() => void accept()}
								>
									Accept
									<ButtonKeys>1</ButtonKeys>
								</Button>
							</DecisionBar>
						)}
					</div>
				</AgentMessage>
			</div>
		)
	},
)
